import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import React from "react";

// Validation Schemas
export const heartRateSchema = z.object({
  value: z.string().min(1, "Heart rate is required"),
  date: z.string().optional(),
});

export const weightSchema = z.object({
  value: z.string().min(1, "Weight is required"),
  unit: z.enum(["kg", "lbs"]),
  date: z.string().optional(),
});

export const bloodPressureSchema = z.object({
  systolic: z.string().min(1, "Systolic pressure is required"),
  diastolic: z.string().min(1, "Diastolic pressure is required"),
  date: z.string().optional(),
});

export const temperatureSchema = z.object({
  value: z.string().min(1, "Temperature is required"),
  unit: z.enum(["C", "F"]),
  date: z.string().optional(),
});

export const respiratoryRateSchema = z.object({
  value: z.string().min(1, "Respiratory rate is required"),
  date: z.string().optional(),
});

export const oxygenSaturationSchema = z.object({
  value: z.string().min(1, "Oxygen saturation is required"),
  date: z.string().optional(),
});

export const newsPainScoreSchema = z.object({
  value: z.string().min(1, "NEWS pain score is required"),
  date: z.string().optional(),
});

export const bloodSugarSchema = z.object({
  value: z.string().min(1, "Blood sugar is required"),
  unit: z.enum(["mg/dL", "mmol/L"]),
  date: z.string().optional(),
});

export const consciousnessLevelSchema = z.object({
  value: z.string().min(1, "Consciousness level is required"),
  date: z.string().optional(),
});

export const supplementalOxygenSchema = z.object({
  value: z.string().min(1, "Please select yes or no"),
  date: z.string().optional(),
});

export const heightSchema = z
  .object({
    value: z.string().optional(),
    unit: z.enum(["cm", "ft/in"]),
    feet: z.string().optional(),
    inches: z.string().optional(),
    date: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    // CM selected → height is required
    if (data.unit === "cm") {
      if (!data.value || Number(data.value) <= 0) {
        ctx.addIssue({
          path: ["value"],
          message: "Height is required",
          code: z.ZodIssueCode.custom,
        });
      }
    }

    // FT/IN selected → optional but at least feet recommended
    if (data.unit === "ft/in") {
      if (!data.feet && !data.inches) {
        ctx.addIssue({
          path: ["feet"],
          message: "Please enter height in feet/inches",
          code: z.ZodIssueCode.custom,
        });
      }
    }
  });

export const circumferenceSchema = z.object({
  waist: z.string().optional(),
  hip: z.string().optional(),
  neck: z.string().optional(),
  unit: z.enum(["cm", "in"]),
  date: z.string().optional(),
});

export const bmiSchema = z
  .object({
    height: z.string().optional(),
    weight: z.string().min(1, "Weight is required"),
    heightUnit: z.enum(["cm", "ft/in"]),
    weightUnit: z.enum(["kg", "lbs"]),
    feet: z.string().optional(),
    inch: z.string().optional(),
    date: z.string().optional(),
  })
  .refine(
    (data) =>
      (data.heightUnit === "cm" && !!data.height) ||
      (data.heightUnit === "ft/in" && !!data.feet),
    {
      message: "Height or feet value is required",
      path: ["height"], // attaches error to height field
    },
  );

// Types
type HeartRateForm = z.infer<typeof heartRateSchema>;
type WeightForm = z.infer<typeof weightSchema>;
type BloodPressureForm = z.infer<typeof bloodPressureSchema>;
type TemperatureForm = z.infer<typeof temperatureSchema>;
type HeightForm = z.infer<typeof heightSchema>;
type CircumferenceForm = z.infer<typeof circumferenceSchema>;
type BMIForm = z.infer<typeof bmiSchema>;
type RespiratoryRateForm = z.infer<typeof respiratoryRateSchema>;
type OxygenSaturationForm = z.infer<typeof oxygenSaturationSchema>;
type NewsPainScoreForm = z.infer<typeof newsPainScoreSchema>;
type BloodSugarForm = z.infer<typeof bloodSugarSchema>;
type ConsciousnessLevelForm = z.infer<typeof consciousnessLevelSchema>;
type SupplementalOxygenForm = z.infer<typeof supplementalOxygenSchema>;
export type VitalType =
  | "heart-rate"
  | "weight"
  | "blood-pressure"
  | "temperature"
  | "height"
  | "circumference"
  | "bmi"
  | "respiratory-rate"
  | "oxygen-saturation"
  | "news-pain-score"
  | "blood-sugar"
  | "consciousness-level"
  | "supplemental-oxygen";

// Static Data
export const vitalTypes = [
  { value: "heart-rate", label: "Heart Rate", icon: "Heart" },
  { value: "weight", label: "Weight", icon: "Weight" },
  { value: "blood-pressure", label: "Blood Pressure", icon: "Activity" },
  { value: "temperature", label: "Temperature", icon: "Thermometer" },
  { value: "respiratory-rate", label: "Respiratory Rate", icon: "Wind" },
  { value: "height", label: "Height", icon: "Ruler" },
  { value: "circumference", label: "Circumference", icon: "Circle" },
  { value: "bmi", label: "BMI Calculator", icon: "Calculator" },
];

export const additionalVitalTypes = [
  { value: "oxygen-saturation", label: "Oxygen Saturation", icon: "Gauge" },
  { value: "news-pain-score", label: "NEWS Pain Score", icon: "AlertCircle" },
  { value: "blood-sugar", label: "Blood Sugar", icon: "Droplets" },
  {
    value: "consciousness-level",
    label: "Consciousness (AVPU)",
    icon: "Activity",
  },
  { value: "supplemental-oxygen", label: "Supplemental Oxygen", icon: "Wind" },
];

export const allVitalTypes = [...vitalTypes, ...additionalVitalTypes];

export const weightUnits = [
  { value: "kg", label: "kg" },
  { value: "lbs", label: "lbs" },
];

export const temperatureUnits = [
  { value: "C", label: "°C" },
  { value: "F", label: "°F" },
];

export const heightUnits = [
  { value: "cm", label: "cm" },
  { value: "ft/in", label: "ft/in" },
];

export const circumferenceUnits = [
  { value: "cm", label: "cm" },
  { value: "in", label: "in" },
];

export const bloodSugarUnits = [
  { value: "mg/dL", label: "mg/dL" },
  { value: "mmol/L", label: "mmol/L" },
];

// Default Values
export const getDefaultValues = (vitalType: VitalType) => {
  const today = new Date();
  const todayDateInput = today.toISOString().slice(0, 10); // YYYY-MM-DD

  switch (vitalType) {
    case "heart-rate":
      return { date: todayDateInput };
    case "weight":
      return { unit: "kg", date: todayDateInput };
    case "blood-pressure":
      return { date: todayDateInput };
    case "temperature":
      return { unit: "C", date: todayDateInput };
    case "respiratory-rate":
      return { date: todayDateInput };
    case "oxygen-saturation":
      return { date: todayDateInput };
    case "news-pain-score":
      return { date: todayDateInput };
    case "blood-sugar":
      return { unit: "mg/dL", date: todayDateInput };
    case "height":
      return { unit: "cm", date: todayDateInput };
    case "circumference":
      return { unit: "cm", date: todayDateInput };
    case "bmi":
      return { heightUnit: "cm", weightUnit: "kg", date: todayDateInput };
    case "consciousness-level":
      return { value: "Alert", date: todayDateInput };
    case "supplemental-oxygen":
      return { value: "No", date: todayDateInput };
    default:
      return { date: todayDateInput };
  }
};

// Schema Selector
export const getSchema = (vitalType: VitalType) => {
  switch (vitalType) {
    case "heart-rate":
      return heartRateSchema;
    case "weight":
      return weightSchema;
    case "blood-pressure":
      return bloodPressureSchema;
    case "temperature":
      return temperatureSchema;
    case "respiratory-rate":
      return respiratoryRateSchema;
    case "oxygen-saturation":
      return oxygenSaturationSchema;
    case "news-pain-score":
      return newsPainScoreSchema;
    case "blood-sugar":
      return bloodSugarSchema;
    case "height":
      return heightSchema;
    case "circumference":
      return circumferenceSchema;
    case "bmi":
      return bmiSchema;
    case "consciousness-level":
      return consciousnessLevelSchema;
    case "supplemental-oxygen":
      return supplementalOxygenSchema;
    default:
      return heartRateSchema;
  }
};

// Custom Hook for Vitals Form State
export const useVitalsFormState = (vitalType: VitalType) => {
  const [open, setOpen] = useState(false);
  const schema = getSchema(vitalType);
  const defaultValues = getDefaultValues(vitalType);

  const formMethods = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  return {
    open,
    setOpen,
    ...formMethods,
    getDefaultValues,
  };
};

// Business Logic Functions
export const handleSubmit = (
  data: any,
  vitalType: VitalType,
  onSave: (vital: any) => void,
  setOpen: (open: boolean) => void,
  reset: () => void,
) => {
  const vitalData = {
    type: vitalType,
    data,
    timestamp: data.date,
  };

  onSave(vitalData);
  setOpen(false);
  reset();
};

// Vital Type Display Functions
export const getVitalTypeLabel = (vitalType: VitalType): string => {
  const vital = allVitalTypes.find((v) => v.value === vitalType);
  return vital?.label || "";
};

export const getVitalTypeIcon = (vitalType: VitalType): string => {
  const vital = allVitalTypes.find((v) => v.value === vitalType);
  return vital?.icon || "Heart";
};

export const useBMIHeightPrefill = (
  vitalType: VitalType,
  open: boolean,
  patientVitals: any,
  setValue: any,
  reset: any,
) => {
  const parseFeetInches = (value?: string) => {
    if (!value) return { feet: "", inches: "" };
    const match = value.match(/(\d+)\s*ft\s*(\d+)?\s*in?/i);
    return {
      feet: match?.[1] ?? "",
      inches: match?.[2] ?? "",
    };
  };

  React.useEffect(() => {
    if (!open || vitalType !== "bmi") return;
    const lastHeight = patientVitals?.height;
    const lastWeight = patientVitals?.weight;

    const defaultValues = {
      ...getDefaultValues("bmi"),
      weight: lastWeight?.value || "",
      weightUnit: lastWeight?.unit || "kg",
    };

    if (lastHeight) {
      if (lastHeight.unit === "cm" && lastHeight.value) {
        // CM: reset is fine because the height field is always visible
        reset({
          ...defaultValues,
          heightUnit: "cm",
          height: lastHeight.value,
        });
      } else if (lastHeight.unit === "ft/in") {
        const { feet, inches } = parseFeetInches(lastHeight.value);
        reset({
          ...defaultValues,
          heightUnit: "ft/in",
          feet: feet,
          inch: inches,
        });
      } else {
        reset(defaultValues);
      }
    } else if (lastWeight) {
      reset(defaultValues);
    }
  }, [
    open,
    vitalType,
    patientVitals?.height,
    patientVitals?.weight,
    reset,
    setValue,
  ]);
};

export const useHeightUnitSwitch = (
  unit: string | undefined,
  setValue: any,
  clearErrors: any,
) => {
  React.useEffect(() => {
    if (unit === "ft/in") {
      setValue("value", "");
      clearErrors("value");
    }
  }, [unit, setValue, clearErrors]);
};

// -------------------------Calculators data---------------------------------------------------

// NICE guidelines and evidence-based BMI thresholds for different ethnicities
// Based on type 2 diabetes risk equivalent to standard thresholds in White populations
export const getBMIThresholds = (ethnicity?: string) => {
  // Standard thresholds for White populations
  const whiteThresholds = {
    underweight: 18.5,
    normal: 25,
    overweight: 30,
  };

  // Lower thresholds for non-White populations - NICE guideline
  // Reference: NICE NG246, Lancet Diabetes Endocrinol 2021
  const nonWhiteThresholds = {
    underweight: 18.5,
    normal: 23, // NICE guideline for overweight threshold
    overweight: 27.5, // NICE guideline for obesity threshold
  };

  if (!ethnicity) return whiteThresholds;

  // Check if ethnicity is White
  if (ethnicity.startsWith("White -")) {
    return whiteThresholds;
  }

  // All other ethnicities (Asian, Black, Mixed, Chinese, Other, etc.) use lower thresholds
  return nonWhiteThresholds;
};

export interface BmiPlausibilityResult {
  tier: "silent_accept" | "confirmation_prompt" | "hard_reject";
  message?: string;
}

export const evaluateBmiPlausibility = (bmi: number): BmiPlausibilityResult => {
  if (bmi < 10 || bmi > 100) {
    return {
      tier: "hard_reject",
      message:
        "This BMI is outside the physiologically possible range. This usually means a height or weight was entered in the wrong unit — please check the units selected above.",
    };
  }

  if (bmi < 15 || bmi > 60) {
    return {
      tier: "confirmation_prompt",
      message: `A BMI of ${bmi.toFixed(1)} is unusual. Please double-check the height and weight units before confirming.`,
    };
  }

  return { tier: "silent_accept" };
};

export const evaluateWeightPlausibility = (
  value: number,
  unit: "kg" | "lbs",
): BmiPlausibilityResult => {
  const weightInKg = unit === "lbs" ? value * 0.453592 : value;

  if (weightInKg < 2 || weightInKg > 300) {
    return {
      tier: "hard_reject",
      message: `A weight of ${value} ${unit} is outside the physiologically possible range. This usually means the value was entered in the wrong unit — please check the unit selected above.`,
    };
  }

  if (weightInKg < 30 || weightInKg > 200) {
    return {
      tier: "confirmation_prompt",
      message: `A weight of ${value} ${unit} is unusual. Please double-check the unit selected before confirming.`,
    };
  }

  return { tier: "silent_accept" };
};


// Get BMI category based on ethnicity-specific thresholds
export const getBMICategoryByEthnicity = (
  bmi: number,
  ethnicity?: string,
): string => {
  const thresholds = getBMIThresholds(ethnicity);

  if (bmi < thresholds.underweight) return "Underweight";
  if (bmi < thresholds.normal) return "Normal weight";
  if (bmi < thresholds.overweight) return "Overweight";
  return "Obese";
};

export const ethnicityOptions = [
  { value: "Not Specified", label: "Not Specified" },
  { value: "White - British", label: "White - British" },
  { value: "White - Irish", label: "White - Irish" },
  {
    value: "White - Any other White background",
    label: "White - Any other White background",
  },
  {
    value: "Mixed - White and Black Caribbean",
    label: "Mixed - White and Black Caribbean",
  },
  {
    value: "Mixed - White and Black African",
    label: "Mixed - White and Black African",
  },
  { value: "Mixed - White and Asian", label: "Mixed - White and Asian" },
  {
    value: "Mixed - Any other mixed background",
    label: "Mixed - Any other mixed background",
  },
  {
    value: "Asian or Asian British - Indian",
    label: "Asian or Asian British - Indian",
  },
  {
    value: "Asian or Asian British - Pakistani",
    label: "Asian or Asian British - Pakistani",
  },
  {
    value: "Asian or Asian British - Bangladeshi",
    label: "Asian or Asian British - Bangladeshi",
  },
  {
    value: "Asian or Asian British - Any other Asian background",
    label: "Asian or Asian British - Any other Asian background",
  },
  {
    value: "Black or Black British - Caribbean",
    label: "Black or Black British - Caribbean",
  },
  {
    value: "Black or Black British - African",
    label: "Black or Black British - African",
  },
  {
    value: "Black or Black British - Any other Black background",
    label: "Black or Black British - Any other Black background",
  },
  {
    value: "Other Ethnic Groups - Chinese",
    label: "Other Ethnic Groups - Chinese",
  },
  {
    value: "Other Ethnic Groups - Any other ethnic group",
    label: "Other Ethnic Groups - Any other ethnic group",
  },
  { value: "Not stated", label: "Not stated" },
  { value: "Not known", label: "Not known" },
];
